import { createContext, useContext, useState, useEffect, useRef } from 'react';
import portfolioService from '@services/portfolio.service';
import { useReports } from '@contexts/ReportsContext';

const PortfolioContext = createContext();

// Global flag to prevent multiple context instances from fetching simultaneously
let globalFetchInProgress = false;
let globalHasFetched = false;

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

export const PortfolioProvider = ({ children }) => {
  const [careerStage, setCareerStage] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSpecialisation, setSelectedSpecialisation] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [coursePreference, setCoursePreference] = useState('');
  const [abroadEducation, setAbroadEducation] = useState(false);
  const [medicalIssue, setMedicalIssue] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedUniversities, setSelectedUniversities] = useState([]);
  const [examType, setExamType] = useState('');
  const [customExam, setCustomExam] = useState('');
  const [selectedExams, setSelectedExams] = useState([]);
  const [selectedAptitudeTopics, setSelectedAptitudeTopics] = useState([]);
  const [opportunityType, setOpportunityType] = useState('');
  const [selectedPersonalityTraits, setSelectedPersonalityTraits] = useState([]);
  const [selectedCareerDomains, setSelectedCareerDomains] = useState([]);

  // Fresher-specific state
  const [pursuingCourse, setPursuingCourse] = useState('');
  const [courseDuration, setCourseDuration] = useState('');
  const [campusName, setCampusName] = useState('');
  const [fresherAddress, setFresherAddress] = useState('');
  const [fresherState, setFresherState] = useState('');
  const [fresherDistrict, setFresherDistrict] = useState('');
  const [cgpaRange, setCgpaRange] = useState('');
  const [selectedIndustryOutlook, setSelectedIndustryOutlook] = useState([]);
  const [jobSector, setJobSector] = useState('');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [resumeReady, setResumeReady] = useState('');
  const [resumeFile, setResumeFile] = useState(null);

  // Working Professional-specific state
  const [professionalTier, setProfessionalTier] = useState('');
  const [industryFocus, setIndustryFocus] = useState('');
  const [professionalSoftSkills, setProfessionalSoftSkills] = useState([]);
  const [careerSwitchPreferences, setCareerSwitchPreferences] = useState([]);
  const [selectedHardSkills, setSelectedHardSkills] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('');

  const [courses, setCourses] = useState([]);
  const [specialisations, setSpecialisations] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [states, setStates] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [personalityTraits, setPersonalityTraits] = useState([]);
  const [careerDomains, setCareerDomains] = useState([]);

  // Fresher-specific data sources
  const [skillsList, setSkillsList] = useState([]);
  const [jobRolesList, setJobRolesList] = useState([]);
  const [industriesList, setIndustriesList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

  // ── Multi-report integration ────────────────────────────────────────────
  const { activeReportId, activeReport, updateActiveReportData, ensureFirstReport, loading: reportsLoading, reports } =
    useReports();

  // Prevent auto-save from firing while we are loading a report's data
  const isLoadingFromReport = useRef(false);
  const loadTimeoutRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Create first report when user starts filling data (only if no reports exist and not loading)
  useEffect(() => {
    const createFirstReportIfNeeded = async () => {
      // Wait for reports to load first
      if (reportsLoading) return;
      
      // Only create if no reports exist and user has selected a career stage
      if (!activeReportId && careerStage && reports.length === 0) {
        await ensureFirstReport();
      }
    };
    
    createFirstReportIfNeeded();
  }, [careerStage, activeReportId, reports.length, reportsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the active report changes → batch-load its data into state
  useEffect(() => {
    if (!activeReport || !activeReport.data) return;

    isLoadingFromReport.current = true;
    clearTimeout(loadTimeoutRef.current);

    const d = activeReport.data;
    setCareerStage(d.careerStage ?? '');
    setSelectedCourse(d.selectedCourse ?? null);
    setSelectedSpecialisation(d.selectedSpecialisation ?? null);
    setSelectedSubjects(d.selectedSubjects ?? []);
    setCoursePreference(d.coursePreference ?? '');
    setAbroadEducation(d.abroadEducation ?? false);
    setMedicalIssue(d.medicalIssue ?? false);
    setSelectedState(d.selectedState ?? null);
    setSelectedUniversities(d.selectedUniversities ?? []);
    setExamType(d.examType ?? '');
    setCustomExam(d.customExam ?? '');
    setSelectedExams(d.selectedExams ?? []);
    setSelectedAptitudeTopics(d.selectedAptitudeTopics ?? []);
    setOpportunityType(d.opportunityType ?? '');
    setSelectedPersonalityTraits(d.selectedPersonalityTraits ?? []);
    setSelectedCareerDomains(d.selectedCareerDomains ?? []);
    // Fresher-specific
    setPursuingCourse(d.pursuingCourse ?? '');
    setCourseDuration(d.courseDuration ?? '');
    setCampusName(d.campusName ?? '');
    setFresherAddress(d.fresherAddress ?? '');
    setFresherState(d.fresherState ?? '');
    setFresherDistrict(d.fresherDistrict ?? '');
    setCgpaRange(d.cgpaRange ?? '');
    setSelectedIndustryOutlook(d.selectedIndustryOutlook ?? []);
    setJobSector(d.jobSector ?? '');
    setSelectedJobs(d.selectedJobs ?? []);
    setResumeReady(d.resumeReady ?? '');
    setResumeFile(d.resumeFile ?? null);
    // Working Professional-specific
    setProfessionalTier(d.professionalTier ?? '');
    setIndustryFocus(d.industryFocus ?? '');
    setProfessionalSoftSkills(d.professionalSoftSkills ?? []);
    setCareerSwitchPreferences(d.careerSwitchPreferences ?? []);
    setSelectedHardSkills(d.selectedHardSkills ?? []);
    setExperienceLevel(d.experienceLevel ?? '');

    // Fetch appropriate data sources for the loaded profile type
    if (d.careerStage === 'fresher') {
      fetchFresherData();
    } else if (d.careerStage === 'professional') {
      fetchProfessionalData();
    } else {
      fetchInitialData();
      if (d.coursePreference) fetchDegrees(d.coursePreference);
      if (d.selectedState) fetchUniversitiesByState(d.selectedState);
    }

    // Release the loading lock after all state batches settle
    loadTimeoutRef.current = setTimeout(() => {
      isLoadingFromReport.current = false;
    }, 150);
  }, [activeReportId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ── end multi-report integration ────────────────────────────────────────

  useEffect(() => {
    if (!hasInitialDataLoaded) {
      fetchInitialData();
    }
  }, [hasInitialDataLoaded]);

  // Cleanup: Reset flags only when component unmounts
  useEffect(() => {
    return () => {
      globalFetchInProgress = false;
      globalHasFetched = false;
    };
  }, []);

  useEffect(() => {
    fetchDegrees(coursePreference);
    if (!isLoadingFromReport.current) {
      setSelectedCourse(null);
      setSelectedSpecialisation(null);
      setSelectedSubjects([]);
      setSpecialisations([]);
      setSubjects([]);
    }
  }, [coursePreference]);

  useEffect(() => {
    if (selectedState) {
      fetchUniversitiesByState(selectedState);
    } else {
      setUniversities([]);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedCourse) {
      fetchSpecialisationsByCourse(selectedCourse);
    } else if (!isLoadingFromReport.current) {
      setSpecialisations([]);
      setSelectedSpecialisation(null);
      setSubjects([]);
      setSelectedSubjects([]);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedSpecialisation) {
      const specialisation = specialisations.find(s => s.id === selectedSpecialisation);
      if (specialisation && specialisation.subjects) {
        const subjectsArray = Array.isArray(specialisation.subjects) 
          ? specialisation.subjects 
          : JSON.parse(specialisation.subjects || '[]');
        setSubjects(subjectsArray.map((name, index) => ({ id: index + 1, name })));
      }
    } else if (!isLoadingFromReport.current) {
      setSubjects([]);
      setSelectedSubjects([]);
    }
  }, [selectedSpecialisation, specialisations]);

  const fetchInitialData = async () => {
    // Prevent multiple simultaneous fetches
    if (globalFetchInProgress || globalHasFetched) {
      return;
    }

    try {
      globalFetchInProgress = true;
      setLoading(true);
      const [statesRes, traitsRes, domainsRes] = await Promise.all([
        portfolioService.getStates(),
        portfolioService.getPersonalityTraits(),
        portfolioService.getCareerDomains('student')
      ]);

      if (statesRes.success && Array.isArray(statesRes?.data?.data)) {
        setStates(statesRes.data.data);
      }

      if (traitsRes.success && Array.isArray(traitsRes?.data?.data)) {
        setPersonalityTraits(traitsRes.data.data);
      }

      if (domainsRes.success && Array.isArray(domainsRes?.data?.data)) {
        setCareerDomains(domainsRes.data.data);
      }
      globalHasFetched = true;
      setHasInitialDataLoaded(true);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      
      const errorStatus = error.response?.status;
      if (errorStatus >= 500) {
        console.error('Server error detected, blocking further attempts');
      } else if (errorStatus === 429) {
        console.error('Rate limit exceeded');
      } else if (!error.response) {
        console.error('Network error - cannot connect to server');
      }
      
      setHasInitialDataLoaded(true);
    } finally {
      setLoading(false);
      globalFetchInProgress = false;
    }
  };

  const fetchDegrees = async (preference) => {
    try {
      const response = await portfolioService.getDegrees(preference || undefined);
      if (response.success && Array.isArray(response?.data?.data)) {
        setCourses(response.data.data);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error('Error fetching degrees:', error);
      setCourses([]);
    }
  };

  const fetchUniversitiesByState = async (state) => {
    try {
      const response = await portfolioService.getUniversitiesByState(state);
      if (response.success && Array.isArray(response?.data?.data)) {
        setUniversities(response.data.data);
      } else {
        setUniversities([]);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
      setUniversities([]);
    }
  };

  const fetchFresherData = async () => {
    try {
      setLoading(true);
      const [traitsRes, domainsRes, industriesRes] = await Promise.all([
        portfolioService.getPersonalityTraits(),
        portfolioService.getCareerDomains('fresher'),
        portfolioService.getIndustriesByUserStage('fresher')
      ]);

      if (traitsRes.success && Array.isArray(traitsRes?.data?.data)) {
        setPersonalityTraits(traitsRes.data.data);
      }

      if (domainsRes.success && Array.isArray(domainsRes?.data?.data)) {
        setCareerDomains(domainsRes.data.data);
      }

      if (industriesRes.success && Array.isArray(industriesRes?.data?.data)) {
        setIndustriesList(industriesRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching fresher data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfessionalData = async () => {
    try {
      setLoading(true);
      const [traitsRes, domainsRes] = await Promise.all([
        portfolioService.getPersonalityTraits(),
        portfolioService.getCareerDomains('professional')
      ]);

      if (traitsRes.success && Array.isArray(traitsRes?.data?.data)) {
        setPersonalityTraits(traitsRes.data.data);
      }

      if (domainsRes.success && Array.isArray(domainsRes?.data?.data)) {
        setCareerDomains(domainsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching professional data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialisationsByCourse = async (courseId) => {
    try {
      const course = courses.find(c => c.id === courseId);
      if (!course) return;

      const response = await portfolioService.getSpecialisationsByDegree(course.name);
      if (response.success && Array.isArray(response?.data?.data)) {
        setSpecialisations(response.data.data);
      } else {
        setSpecialisations([]);
      }
    } catch (error) {
      console.error('Error fetching specialisations:', error);
      setSpecialisations([]);
    }
  };

  const updateCareerStage = (stage) => {
    // Check if profile type is locked in the active report
    if (activeReport?.isProfileTypeLocked && stage !== careerStage) {
      console.warn('Profile type is locked. Generate a new report to change profile type.');
      return;
    }
    
    if (stage !== careerStage) {
      // Reset all profile-specific data when switching profile type
      resetProfileData();
      setCareerStage(stage);
      
      // Fetch profile-specific data
      if (stage === 'fresher') {
        fetchFresherData();
      } else if (stage === 'professional') {
        fetchProfessionalData();
      } else if (stage === 'student') {
        fetchInitialData();
      }
    }
  };

  const resetProfileData = () => {
    // Reset common fields
    setSelectedCourse(null);
    setSelectedSpecialisation(null);
    setSelectedSubjects([]);
    setCoursePreference('');
    setAbroadEducation(false);
    setMedicalIssue(false);
    setSelectedState(null);
    setSelectedUniversities([]);
    setExamType('');
    setCustomExam('');
    setSelectedExams([]);
    setSelectedJobs([]);
    setSelectedIndustryOutlook([]);
    setJobSector('');
    setSelectedAptitudeTopics([]);
    setResumeReady('');
    setResumeFile(null);
    setOpportunityType('');
    setSelectedPersonalityTraits([]);
    setSelectedCareerDomains([]);
    
    // Reset fresher-specific fields
    setPursuingCourse('');
    setCourseDuration('');
    setCampusName('');
    setFresherAddress('');
    setFresherState('');
    setFresherDistrict('');
    setCgpaRange('');
    setResumeReady('');
    setResumeFile(null);
    
    // Reset working professional-specific fields
    setProfessionalTier('');
    setIndustryFocus('');
    setProfessionalSoftSkills([]);
    setCareerSwitchPreferences([]);
    setSelectedHardSkills([]);
    setExperienceLevel('');
  };

  // ── Auto-save: push any state change into the active report (debounced) ──
  useEffect(() => {
    if (isLoadingFromReport.current || !activeReportId) return;

    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const currentData = {
        careerStage,
        selectedCourse,
        selectedSpecialisation,
        selectedSubjects,
        coursePreference,
        abroadEducation,
        medicalIssue,
        selectedState,
        selectedUniversities,
        examType,
        customExam,
        selectedExams,
        selectedAptitudeTopics,
        opportunityType,
        selectedPersonalityTraits,
        selectedCareerDomains,
        pursuingCourse,
        courseDuration,
        campusName,
        fresherAddress,
        fresherState,
        fresherDistrict,
        cgpaRange,
        selectedIndustryOutlook,
        jobSector,
        selectedJobs,
        resumeReady,
        resumeFile,
        professionalTier,
        industryFocus,
        professionalSoftSkills,
        careerSwitchPreferences,
        selectedHardSkills,
        experienceLevel,
      };

      // Compute completion info so ReportCard progress bar stays accurate
      let completedSteps = 0;
      let totalSteps = 8;
      
      if (careerStage === 'fresher') {
        totalSteps = 8;
        if (careerStage) completedSteps++;
        if (pursuingCourse && courseDuration && campusName) completedSteps++;
        if (fresherAddress && fresherState && fresherDistrict) completedSteps++;
        if (cgpaRange) completedSteps++;
        if (selectedPersonalityTraits.length > 0) completedSteps++;
        if (selectedExams.length > 0 || selectedAptitudeTopics.length > 0) completedSteps++;
        if (jobSector) completedSteps++;
        if (selectedJobs.length > 0 || selectedIndustryOutlook.length > 0) completedSteps++;
      } else if (careerStage === 'professional') {
        // Professional has 5 steps, or 6 if Career Switch
        totalSteps = professionalTier === 'Career Switch' ? 6 : 5;
        if (careerStage) completedSteps++;
        if (professionalTier) completedSteps++;
        if (industryFocus) completedSteps++;
        if (professionalTier === 'Career Switch' && careerSwitchPreferences.length > 0) completedSteps++;
        if (experienceLevel) completedSteps++;
        if (selectedHardSkills.length > 0) completedSteps++;
      } else {
        totalSteps = 8;
        if (careerStage) completedSteps++;
        if (selectedCourse) completedSteps++;
        if (selectedSubjects.length > 0) completedSteps++;
        if (selectedState) completedSteps++;
        if (selectedUniversities.length > 0) completedSteps++;
        if (opportunityType) completedSteps++;
        if (selectedPersonalityTraits.length > 0) completedSteps++;
        if (selectedCareerDomains.length > 0) completedSteps++;
      }

      updateActiveReportData(currentData, { completed: completedSteps, total: totalSteps });
    }, 300);
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    careerStage, selectedCourse, selectedSpecialisation, selectedSubjects,
    coursePreference, abroadEducation, medicalIssue, selectedState,
    selectedUniversities, examType, customExam, selectedExams,
    selectedAptitudeTopics, opportunityType, selectedPersonalityTraits,
    selectedCareerDomains, pursuingCourse, courseDuration, campusName,
    fresherAddress, fresherState, fresherDistrict, cgpaRange,
    selectedIndustryOutlook, jobSector, selectedJobs, resumeReady, resumeFile,
    professionalTier, industryFocus, professionalSoftSkills, careerSwitchPreferences,
    selectedHardSkills, experienceLevel,
  ]);
  // ── end auto-save ─────────────────────────────────────────────────────

  const updateCourse = (course) => {
    setSelectedCourse(course);
  };

  const updateSpecialisation = (specialisation) => {
    setSelectedSpecialisation(specialisation);
  };

  const updateSubjects = (subjects) => {
    setSelectedSubjects(subjects);
  };

  const updateCoursePreference = (preference) => {
    setCoursePreference(preference);
  };

  const updateAbroadEducation = (value) => {
    setAbroadEducation(value);
  };

  const updateMedicalIssue = (value) => {
    setMedicalIssue(value);
  };

  const updateState = (state) => {
    setSelectedState(state);
  };

  const updateUniversities = (universities) => {
    if (universities.length <= 3) {
      setSelectedUniversities(universities);
    }
  };

  const toggleUniversity = (university, state) => {
    setSelectedUniversities((prev) => {
      const exists = prev.find((u) => u.id === university.id);
      if (exists) {
        return prev.filter((u) => u.id !== university.id);
      }
      if (prev.length >= 3) return prev;
      return [
        ...prev,
        {
          id: university.id,
          universityName: university.universityName,
          state: state || university.state || null,
        },
      ];
    });
  };

  const updateExamType = (type) => {
    setExamType(type);
    if (type !== 'custom' && type !== 'other') {
      setCustomExam('');
    }
  };

  const updateCustomExam = (exam) => {
    setCustomExam(exam);
  };

  const updateOpportunityType = (type) => {
    setOpportunityType(type);
  };

  const toggleExam = (exam) => {
    setSelectedExams((prev) => {
      const exists = prev.find((e) => e.id === exam.id);
      if (exists) {
        return prev.filter((e) => e.id !== exam.id);
      }
      return [
        ...prev,
        {
          id: exam.id,
          examName: exam.examName,
          fullForm: exam.fullForm,
          category: exam.category,
          purpose: exam.purpose,
          eligibility: exam.eligibility,
        },
      ];
    });
  };

  const updateSelectedExams = (exams) => {
    setSelectedExams(exams);
  };

  const toggleJob = (job) => {
    setSelectedJobs((prev) => {
      const exists = prev.find((j) => j.id === job.id);
      if (exists) {
        return prev.filter((j) => j.id !== job.id);
      }
      return [
        ...prev,
        {
          id: job.id,
          role: job.role || job.title || job.jobName,
          title: job.title,
          sector: job.sector,
          industry: job.industry,
          category: job.category,
          dept_org: job.dept_org,
          skills: job.skills,
          tools: job.tools,
          useCase: job.useCase,
          experience: job.experience,
          level: job.level,
        },
      ];
    });
  };

  const updateSelectedJobs = (jobs) => {
    setSelectedJobs(jobs);
  };

  const toggleAptitudeTopic = (topic) => {
    setSelectedAptitudeTopics((prev) => {
      const exists = prev.find((t) => t.id === topic.id);
      if (exists) {
        return prev.filter((t) => t.id !== topic.id);
      }
      return [
        ...prev,
        {
          id: topic.id,
          topic: topic.topic,
          category: topic.category,
          subdomain: topic.subdomain,
        },
      ];
    });
  };

  const updateSelectedAptitudeTopics = (topics) => {
    setSelectedAptitudeTopics(topics);
  };

  const updatePersonalityTraits = (traits) => {
    if (traits.length <= 20) {
      setSelectedPersonalityTraits(traits);
    }
  };

  const updateCareerDomains = (domains) => {
    setSelectedCareerDomains(domains);
  };

  const resetPortfolio = () => {
    setCareerStage('');
    resetProfileData();
  };

  // Fresher-specific update functions
  const updatePursuingCourse = (course) => setPursuingCourse(course);
  const updateCourseDuration = (duration) => setCourseDuration(duration);
  const updateCampusName = (campus) => setCampusName(campus);
  const updateFresherAddress = (address) => setFresherAddress(address);
  const updateFresherState = (state) => setFresherState(state);
  const updateFresherDistrict = (district) => setFresherDistrict(district);
  const updateCgpaRange = (range) => setCgpaRange(range);
  const updateSelectedIndustryOutlook = (industries) => setSelectedIndustryOutlook(industries);
  const updateJobSector = (sector) => setJobSector(sector);
  const updateResumeReady = (status) => setResumeReady(status);
  const updateResumeFile = (file) => setResumeFile(file);
  
  const toggleIndustryOutlook = (industry) => {
    setSelectedIndustryOutlook((prev) => {
      const exists = prev.includes(industry);
      if (exists) {
        return prev.filter((i) => i !== industry);
      }
      return [...prev, industry];
    });
  };

  // Working Professional-specific update functions
  const updateProfessionalTier = (tier) => setProfessionalTier(tier);
  const updateIndustryFocus = (industry) => setIndustryFocus(industry);
  const updateProfessionalSoftSkills = (skills) => setProfessionalSoftSkills(skills);
  const updateCareerSwitchPreferences = (prefs) => setCareerSwitchPreferences(prefs);
  const updateSelectedHardSkills = (skills) => setSelectedHardSkills(skills);
  const updateExperienceLevel = (level) => setExperienceLevel(level);

  const toggleProfessionalSoftSkill = (skill) => {
    setProfessionalSoftSkills((prev) => {
      const exists = prev.find((s) => s.id === skill.id);
      if (exists) {
        return prev.filter((s) => s.id !== skill.id);
      }
      return [...prev, skill];
    });
  };

  const toggleCareerSwitchPreference = (pref) => {
    setCareerSwitchPreferences((prev) => {
      const exists = prev.includes(pref);
      if (exists) {
        return prev.filter((p) => p !== pref);
      }
      return [...prev, pref];
    });
  };

  const toggleHardSkill = (skill) => {
    setSelectedHardSkills((prev) => {
      const exists = prev.find((s) => s.id === skill.id);
      if (exists) {
        return prev.filter((s) => s.id !== skill.id);
      }
      if (prev.length >= 20) return prev;
      return [...prev, skill];
    });
  };

  const value = {
    careerStage,
    selectedCourse,
    selectedSpecialisation,
    selectedSubjects,
    coursePreference,
    abroadEducation,
    medicalIssue,
    selectedState,
    selectedUniversities,
    examType,
    customExam,
    selectedExams,
    selectedAptitudeTopics,
    opportunityType,
    selectedPersonalityTraits,
    selectedCareerDomains,
    toggleUniversity,
    courses,
    specialisations,
    subjects,
    states,
    universities,
    personalityTraits,
    careerDomains,
    industriesList,
    loading,
    updateCareerStage,
    updateCourse,
    updateSpecialisation,
    updateSubjects,
    updateCoursePreference,
    updateAbroadEducation,
    updateMedicalIssue,
    updateState,
    updateUniversities,
    updateExamType,
    updateCustomExam,
    toggleExam,
    updateSelectedExams,
    toggleAptitudeTopic,
    updateSelectedAptitudeTopics,
    updateOpportunityType,
    updatePersonalityTraits,
    updateCareerDomains,
    resetPortfolio,
    // Fresher-specific state
    pursuingCourse,
    courseDuration,
    campusName,
    fresherAddress,
    fresherState,
    fresherDistrict,
    cgpaRange,
    selectedIndustryOutlook,
    jobSector,
    selectedJobs,
    resumeReady,
    resumeFile,
    // Fresher-specific update functions
    updatePursuingCourse,
    updateCourseDuration,
    updateCampusName,
    updateFresherAddress,
    updateFresherState,
    updateFresherDistrict,
    updateCgpaRange,
    updateSelectedIndustryOutlook,
    updateJobSector,
    toggleIndustryOutlook,
    toggleJob,
    updateSelectedJobs,
    updateResumeReady,
    updateResumeFile,
    // Working Professional-specific state
    professionalTier,
    industryFocus,
    professionalSoftSkills,
    careerSwitchPreferences,
    selectedHardSkills,
    experienceLevel,
    // Working Professional-specific update functions
    updateProfessionalTier,
    updateIndustryFocus,
    updateProfessionalSoftSkills,
    updateCareerSwitchPreferences,
    updateSelectedHardSkills,
    updateExperienceLevel,
    toggleProfessionalSoftSkill,
    toggleCareerSwitchPreference,
    toggleHardSkill,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};
